import { useMemo, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Loader2, FileText, ImageIcon } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { Form } from '@/components/molecules/form-field';
import { FormPageHeader, FormProgressIndicator } from '@/components/forms';

import {
  ShopBasicInfo,
  ShopLocation,
  ShopSchedule,
  ShopImages,
  useShop,
  useUpdateShop,
  useUpdateShopImages,
  useShopImagesEditor,
  updateShopSchema,
  type UpdateShopFormData,
} from '@/features/shops';

import { ShippingRatesSelector } from '@/features/shipping/components/shipping-rates-selector';
import { useCountries } from '@/lib/hooks/use-countries';
import { DAYS } from '@/lib/constants/form-options';
import type { Country, ShopAddress } from '@/lib/types';

function parseTimeFromISO(isoString?: string): string {
  if (!isoString) return '';
  try {
    if (isoString.includes('T')) {
      const date = new Date(isoString);
      return date.toTimeString().slice(0, 5);
    }
    return isoString;
  } catch {
    return isoString;
  }
}

function isAddressObject(address: ShopAddress | string | undefined): address is ShopAddress {
  return typeof address === 'object' && address !== null;
}

const DEFAULT_AVAILABILITY = {
  enable: true,
  startDateMorning: '08:00',
  closeDateMorning: '12:00',
  startingDateAfternoon: '14:00',
  closeDateAfternoon: '18:00',
};

const DEFAULT_DISABLED = {
  enable: false,
  startDateMorning: '',
  closeDateMorning: '',
  startingDateAfternoon: '',
  closeDateAfternoon: '',
};

export default function EditShopPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();

  const { data: shop, isLoading: isLoadingShop } = useShop(id ?? '');
  const { mutate: updateShop, isPending: isSubmitting } = useUpdateShop();
  const { mutate: updateShopImages, isPending: isUpdatingImages } = useUpdateShopImages();
  const { data: countries = [] } = useCountries();
  const queryClient = useQueryClient();

  // Single source of truth for images
  const {
    images: allImages,
    previews,
    newFiles,
    isCompressing,
    addImages,
    removeImage,
    reorderImages,
    reinitialize,
    hasChanges: hasImageChanges,
    getOrderData,
  } = useShopImagesEditor({
    existingImages: shop?.images,
  });

  const form = useForm<UpdateShopFormData>({
    resolver: zodResolver(updateShopSchema),
    defaultValues: {
      name: '',
      manager: '',
      description: '',
      phoneNumber: '',
      delivery: 'both',
      address: '',
      city: '',
      department: '',
      postalCode: '',
      countryIsoCode3: '',
      latitude: undefined,
      longitude: undefined,
      tags: '',
      shippingRates: [],
      monday: DEFAULT_AVAILABILITY,
      tuesday: DEFAULT_AVAILABILITY,
      wednesday: DEFAULT_AVAILABILITY,
      thursday: DEFAULT_AVAILABILITY,
      friday: DEFAULT_AVAILABILITY,
      saturday: DEFAULT_AVAILABILITY,
      sunday: DEFAULT_DISABLED,
    },
  });

  // Populate form when shop data is loaded
  useEffect(() => {
    if (shop) {
      const address = isAddressObject(shop.address) ? shop.address : null;
      const countryCode = address?.country
        ? typeof address.country === 'object' ? address.country.isoCode3 : address.country
        : '';

      const getDayData = (dayKey: string) => {
        const dayData = (shop as unknown as Record<string, unknown>)[dayKey] as { enable?: boolean; startDateMorning?: string; closeDateMorning?: string; startingDateAfternoon?: string; closeDateAfternoon?: string } | undefined;
        if (!dayData) return DEFAULT_DISABLED;
        return {
          enable: dayData.enable ?? false,
          startDateMorning: parseTimeFromISO(dayData.startDateMorning),
          closeDateMorning: parseTimeFromISO(dayData.closeDateMorning),
          startingDateAfternoon: parseTimeFromISO(dayData.startingDateAfternoon),
          closeDateAfternoon: parseTimeFromISO(dayData.closeDateAfternoon),
        };
      };

      const parsedLatitude = address?.latitude != null ? parseFloat(String(address.latitude)) : undefined;
      const parsedLongitude = address?.longitude != null ? parseFloat(String(address.longitude)) : undefined;

      const shippingRateIds = (shop.shippingRates || []).map((rate: { refId?: string; id?: string }) => rate.refId || rate.id || '');

      form.reset({
        name: shop.name,
        manager: shop.manager || '',
        description: shop.description || '',
        phoneNumber: shop.phoneNumber || shop.phone || '',
        delivery: (shop.delivery as UpdateShopFormData['delivery']) || 'both',
        address: address?.address_line1 || '',
        city: shop.city || address?.city || '',
        department: (address as { state_province?: string })?.state_province || '',
        postalCode: address?.postal_code || '',
        countryIsoCode3: countryCode || '',
        latitude: !isNaN(parsedLatitude as number) ? parsedLatitude : undefined,
        longitude: !isNaN(parsedLongitude as number) ? parsedLongitude : undefined,
        tags: '',
        shippingRates: shippingRateIds,
        monday: getDayData('monday'),
        tuesday: getDayData('tuesday'),
        wednesday: getDayData('wednesday'),
        thursday: getDayData('thursday'),
        friday: getDayData('friday'),
        saturday: getDayData('saturday'),
        sunday: getDayData('sunday'),
      });
    }
  }, [shop, form]);

  const formValues = form.watch();
  const { dirtyFields } = form.formState;

  // Check for changes in information fields
  const hasInformationChanges = Object.keys(dirtyFields).length > 0;

  // Calculate form progress
  const { completedFields, totalFields } = useMemo(() => {
    const requiredFields = ['name', 'manager', 'description', 'phoneNumber', 'delivery', 'countryIsoCode3', 'city', 'address'];
    let completed = requiredFields.filter((field) => {
      const value = formValues[field as keyof UpdateShopFormData];
      return value !== '' && value !== undefined;
    }).length;

    if (allImages.length > 0) completed++;
    const total = requiredFields.length + 1;

    return { completedFields: completed, totalFields: total };
  }, [formValues, allImages.length]);

  // Handle image removal
  const handleImageRemove = (_index: number, imageId?: number | string) => {
    if (imageId !== undefined) {
      removeImage(imageId);
    }
  };

  // Submit information only
  const onSubmitInformation = async (data: UpdateShopFormData) => {
    if (!shop) return;

    const existingAddress = isAddressObject(shop.address) ? shop.address : null;
    const addressId = existingAddress?.id;

    if (!addressId) {
      toast.error(t('common.error'));
      return;
    }

    let countryIsoCode3 = data.countryIsoCode3;
    let countryName = '';

    const selectedCountry = countries.find((c: Country) => c.isoCode3 === countryIsoCode3);
    if (selectedCountry) {
      countryName = selectedCountry.name;
    } else if (existingAddress?.country && typeof existingAddress.country === 'object') {
      countryIsoCode3 = existingAddress.country.isoCode3 || '';
      countryName = existingAddress.country.name || '';
    }

    if (!countryIsoCode3 || !countryName) {
      toast.error(t('shops.countryRequired', { defaultValue: 'Country is required' }));
      return;
    }

    const addressData = {
      id: addressId,
      address_line1: data.address || existingAddress?.address_line1 || '',
      address_line2: data.department || '',
      city: data.city || existingAddress?.city || '',
      postal_code: data.postalCode || '',
      countryIsoCode3,
      countryName,
      latitude: data.latitude,
      longitude: data.longitude,
    };

    const availabilities = DAYS.map((day) => {
      const dayData = data[day];
      return {
        day,
        id: day,
        enable: dayData.enable,
        startDateMorning: dayData.startDateMorning || '08:00',
        closeDateMorning: dayData.closeDateMorning || '12:00',
        startingDateAfternoon: dayData.startingDateAfternoon || '14:00',
        closeDateAfternoon: dayData.closeDateAfternoon || '18:00',
      };
    });

    const updateData = {
      name: data.name,
      manager: data.manager || '',
      phoneNumber: data.phoneNumber || '',
      description: data.description || '',
      address: addressData,
      availabilities,
      shippingRates: data.shippingRates || [],
      delivery: data.delivery,
    };

    const shopId = shop.refId || shop.id;

    updateShop(
      { id: shopId, data: updateData },
      {
        onSuccess: async () => {
          toast.success(t('shops.infoUpdated', { defaultValue: 'Information updated successfully' }));
          await queryClient.refetchQueries({ queryKey: ['shops', id] });
          queryClient.invalidateQueries({ queryKey: ['shops'] });
        },
        onError: () => toast.error(t('common.error')),
      }
    );
  };

  // Submit images only
  const onSubmitImages = () => {
    if (!shop) return;

    if (allImages.length === 0) {
      toast.error(t('shops.imagesRequired'));
      return;
    }

    const shopId = shop.refId || shop.id;
    const customOrders = getOrderData();

    updateShopImages(
      {
        shopId,
        existingImages: shop.images || [],
        imagesToRemove: [],
        newImages: newFiles,
        customOrders,
      },
      {
        onSuccess: async () => {
          toast.success(t('shops.imagesUpdated', { defaultValue: 'Images updated successfully' }));
          // First refetch shop data
          await queryClient.refetchQueries({ queryKey: ['shops', id] });
          queryClient.invalidateQueries({ queryKey: ['shops'] });
          // Then reinitialize images from fresh data
          reinitialize();
        },
        onError: (error) => {
          console.error('[updateShopImages] Error:', error);
          toast.error(t('shops.imagesUpdateFailed', { defaultValue: 'Failed to update images' }));
        },
      }
    );
  };

  if (isLoadingShop) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-9 w-9" />
          <Skeleton className="h-8 w-48" />
        </div>
        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-6">
            <Skeleton className="h-64 w-full" />
            <Skeleton className="h-48 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (!shop) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('shops.error')}</p>
      </div>
    );
  }

  return (
    <div className="pb-6">
      <FormPageHeader
        backLink={`/shops/${id}`}
        title={t('shops.editShop', { defaultValue: 'Edit Shop' })}
        description={shop.name}
      />

      <FormProgressIndicator
        completed={completedFields}
        total={totalFields}
        label={t('shops.fieldsCompleted')}
      />

      <Form {...form}>
        <form onSubmit={(e) => e.preventDefault()}>
          <div className="flex flex-col gap-6 lg:grid lg:grid-cols-3">
            {/* Images - first on mobile */}
            <div className="order-first lg:order-last space-y-6">
              <ShopImages
                previews={previews}
                onAdd={addImages}
                onRemove={handleImageRemove}
                onReorder={reorderImages}
                isCompressing={isCompressing}
              />

              {/* Desktop buttons */}
              <div className="hidden lg:flex lg:flex-col gap-2 sticky bottom-4">
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting || !hasInformationChanges}
                  onClick={form.handleSubmit(onSubmitInformation)}
                >
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isSubmitting && <FileText className="size-4 mr-2" />}
                  {t('shops.updateInformation', { defaultValue: 'Update Information' })}
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  variant="secondary"
                  disabled={isUpdatingImages || !hasImageChanges || isCompressing}
                  onClick={onSubmitImages}
                >
                  {isUpdatingImages && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isUpdatingImages && <ImageIcon className="size-4 mr-2" />}
                  {t('shops.updateImages', { defaultValue: 'Update Images' })}
                </Button>
              </div>
            </div>

            {/* Form fields */}
            <div className="lg:col-span-2 space-y-6">
              <ShopBasicInfo form={form} />
              <ShopLocation form={form} countries={countries} />
              <ShippingRatesSelector shopRefId={shop.refId || shop.id} />
              <ShopSchedule form={form} />

              {/* Mobile buttons */}
              <div className="lg:hidden space-y-2">
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  disabled={isSubmitting || !hasInformationChanges}
                  onClick={form.handleSubmit(onSubmitInformation)}
                >
                  {isSubmitting && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isSubmitting && <FileText className="size-4 mr-2" />}
                  {t('shops.updateInformation', { defaultValue: 'Update Information' })}
                </Button>
                <Button
                  type="button"
                  className="w-full"
                  size="lg"
                  variant="secondary"
                  disabled={isUpdatingImages || !hasImageChanges || isCompressing}
                  onClick={onSubmitImages}
                >
                  {isUpdatingImages && <Loader2 className="size-4 mr-2 animate-spin" />}
                  {!isUpdatingImages && <ImageIcon className="size-4 mr-2" />}
                  {t('shops.updateImages', { defaultValue: 'Update Images' })}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
